import { useState } from "react";
import type { MfgBuildPipelinePhase } from "@/lib/manufacturingClient";
import { useManufacturingBuildPipelinePhaseEditor } from "./useManufacturingBuildPipelinePhaseEditor";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

function PhaseEditorRow({
  phase,
  index,
  total,
  busy,
  onRename,
  onMoveUp,
  onMoveDown,
  onSetInitial,
  onSetBuildComplete,
  onTogglePayTrigger,
  onDelete,
}: {
  phase: MfgBuildPipelinePhase;
  index: number;
  total: number;
  busy: boolean;
  onRename: (name: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSetInitial: () => void;
  onSetBuildComplete: () => void;
  onTogglePayTrigger: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(phase.display_name);
  const dirty = draft.trim() !== phase.display_name;

  return (
    <li className="flex flex-col gap-2 border-b border-gray-200 py-3 last:border-b-0 dark:border-zinc-700 sm:flex-row sm:items-start sm:gap-3">
      <div className="flex shrink-0 flex-col gap-1">
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy || index === 0}
          onClick={onMoveUp}
          title="Move earlier"
        >
          ↑
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          disabled={busy || index >= total - 1}
          onClick={onMoveDown}
          title="Move later"
        >
          ↓
        </button>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={inputClass}
            value={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            aria-label={`Display name for ${phase.phase_key}`}
          />
          {dirty ? (
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={busy || !draft.trim()}
              onClick={() => onRename(draft.trim())}
            >
              Save name
            </button>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 text-[11px] text-gray-500 dark:text-zinc-400">
          <code className="text-xs">{phase.phase_key}</code>
          <span>· seq {phase.sequence_index}</span>
          {phase.is_initial ? (
            <span className="rounded bg-indigo-600 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-white">
              Initial
            </span>
          ) : null}
          {phase.is_build_complete ? (
            <span className="rounded bg-emerald-700 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-white">
              Build complete
            </span>
          ) : null}
          {phase.is_pay_trigger ? (
            <span className="rounded bg-amber-700 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-white">
              Pay trigger
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {!phase.is_initial ? (
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={onSetInitial}>
              Set initial
            </button>
          ) : null}
          {!phase.is_build_complete ? (
            <button
              type="button"
              className={ghostBtnClass}
              disabled={busy}
              onClick={onSetBuildComplete}
            >
              Set build complete
            </button>
          ) : null}
          <button type="button" className={ghostBtnClass} disabled={busy} onClick={onTogglePayTrigger}>
            {phase.is_pay_trigger ? "Clear pay trigger" : "Set pay trigger"}
          </button>
          {!phase.is_initial && !phase.is_build_complete ? (
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={onDelete}>
              Remove
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

export function ManufacturingBuildPipelinePhaseEditor({
  phases,
  onChanged,
}: {
  phases: MfgBuildPipelinePhase[];
  onChanged: () => Promise<void>;
}) {
  const ed = useManufacturingBuildPipelinePhaseEditor(phases, onChanged);
  const ordered = [...phases].sort((a, b) => a.sequence_index - b.sequence_index);

  return (
    <div>
      {ed.error ? (
        <p className="mb-2 text-sm text-red-600 dark:text-red-400" role="alert">
          {ed.error}
        </p>
      ) : null}
      <ul className="mb-4">
        {ordered.map((phase, index) => (
          <PhaseEditorRow
            key={phase.id}
            phase={phase}
            index={index}
            total={ordered.length}
            busy={ed.busy}
            onRename={(name) => void ed.rename(phase.id, name)}
            onMoveUp={() => void ed.move(phase.id, -1)}
            onMoveDown={() => void ed.move(phase.id, 1)}
            onSetInitial={() => void ed.setInitial(phase.id)}
            onSetBuildComplete={() => void ed.setBuildComplete(phase.id)}
            onTogglePayTrigger={() => void ed.setPayTrigger(phase.id, !phase.is_pay_trigger)}
            onDelete={() => void ed.remove(phase.id)}
          />
        ))}
      </ul>
      <div className="flex flex-wrap items-end gap-2 border-t border-gray-200 pt-3 dark:border-zinc-700">
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          Display name
          <input
            className={`${inputClass} mt-1 block`}
            value={ed.newName}
            disabled={ed.busy}
            onChange={(e) => ed.setNewName(e.target.value)}
            placeholder="e.g. Electrical"
          />
        </label>
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          phase_key (optional)
          <input
            className={`${inputClass} mt-1 block`}
            value={ed.newKey}
            disabled={ed.busy}
            onChange={(e) => ed.setNewKey(e.target.value)}
            placeholder="auto from name"
          />
        </label>
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={ed.busy || !ed.newName.trim()}
          onClick={() => void ed.add()}
        >
          Add phase
        </button>
      </div>
    </div>
  );
}
